import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { usePartnerAuth } from '../../context/PartnerAuthContext';
import { usePartnerBooking } from '../../context/PartnerBookingContext';
import { supabase } from '../../lib/supabase';
import { fetchCruiseTimes, fetchBoardingPoint } from '../../lib/cruiseDetails';
import { formatCardDate, displayDeck, cruiseLabel } from '../../components/shared/helpers';
import ModalShell from '../../components/shared/ModalShell';

// "17:00" / "17:00:00" → "5:00 PM"
// (Duplicated from the customer CruiseSummary.js so that file stays untouched.)
function to12h(t) {
  if (!t) return '';
  const [hRaw, mRaw] = String(t).split(':');
  let h = parseInt(hRaw, 10);
  if (Number.isNaN(h)) return '';
  const m = (mRaw ?? '00').padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${m} ${ampm}`;
}

// Local placeholder photos — final fallback when a cruise/deck has no gallery
// rows in media_gallery, so the carousel is never empty.
const GALLERY_PHOTOS = {
  sunsetUpperCover:  { src: '/cruises/sunset-upper-cover.webp',   alt: 'Guests watching the sun set over the Brahmaputra from the open-air upper deck' },
  sunsetMiddleCover: { src: '/cruises/sunset-middle-cover.webp',  alt: 'Covered middle deck with paper lanterns and guests at riverside tables in golden-hour light' },
  sunsetUpperDeck:   { src: '/cruises/sunset-upper-deck.webp',    alt: 'Upper deck lounge seating with gold cushions and planters overlooking the river at sunset' },
  sunsetMiddleDeck:  { src: '/cruises/sunset-middle-deck.webp',   alt: 'Covered middle deck with cane seating and a stage, in warm evening light' },
  sunsetDj:          { src: '/cruises/sunset-dj.webp',            alt: 'Guests dancing under lanterns and coloured stage lights on the covered deck' },
  liveSinging:       { src: '/cruises/live-singing.webp',         alt: 'Singer performing live on the onboard stage' },
  sundownUpperCover: { src: '/cruises/sundown-upper-cover.webp',  alt: 'Open-air upper deck lounge lit by planter lights under a starry night sky' },
  sundownMiddleCover:{ src: '/cruises/sundown-middle-cover.webp', alt: 'Middle deck bar and cane seating glowing under paper lanterns at night' },
  sundownExterior:   { src: '/cruises/sundown-exterior.webp',     alt: 'The Alfresco Grand cruising the Brahmaputra at dusk' },
  sundownDj:         { src: '/cruises/sundown-dj.webp',           alt: 'DJ playing to a full dance floor on the covered deck at night' },
  dinnerCover:       { src: '/cruises/dinner-cover.webp',         alt: 'Bihu dancers in traditional Assamese attire performing on board' },
  dinnerBihu:        { src: '/cruises/dinner-bihu.webp',          alt: 'Bihu dance performance in front of the Alfresco Grand stage' },
  dinnerGayanBayan:  { src: '/cruises/dinner-gayan-bayan.webp',   alt: 'Gayan Bayan drummers in traditional white attire performing on stage' },
  dinnerBuffet:      { src: '/cruises/dinner-buffet.webp',        alt: 'Buffet dinner spread being served in the dining saloon' },
};

function galleryFor(name, deck) {
  const P = GALLERY_PHOTOS;
  if (name === 'Sunset Cruise' && deck === 'Upper Deck') return [P.sunsetUpperCover, P.sunsetUpperDeck, P.sunsetDj, P.liveSinging];
  if (name === 'Sunset Cruise' && deck === 'Middle Deck') return [P.sunsetMiddleCover, P.sunsetMiddleDeck, P.sunsetDj, P.liveSinging];
  if (name === 'Sundown Cruise' && deck === 'Upper Deck') return [P.sundownUpperCover, P.sundownExterior, P.sundownDj, P.liveSinging];
  if (name === 'Sundown Cruise' && deck === 'Middle Deck') return [P.sundownMiddleCover, P.sundownExterior, P.sundownDj, P.liveSinging];
  return [P.dinnerCover, P.dinnerBihu, P.dinnerGayanBayan, P.liveSinging, P.dinnerBuffet]; // Dinner Cruise + Middle Deck
}

// Auto-advancing image carousel with manual arrows and dot indicators.
// (Duplicated from the customer CruiseSummary.js so that file stays untouched.)
function CruiseGallery({ images, heightClass = 'h-[200px] md:h-[220px]' }) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => { setIdx(0); }, [images]);

  useEffect(() => {
    if (paused || images.length <= 1) return;
    const t = setInterval(() => setIdx(i => (i + 1) % images.length), 4000);
    return () => clearInterval(t);
  }, [paused, images]);

  const go = (n) => setIdx((n + images.length) % images.length);

  return (
    <div
      className={`relative w-full ${heightClass} rounded-xl overflow-hidden`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div
        className="flex h-full transition-transform duration-500 ease-[cubic-bezier(0.23,1,0.32,1)]"
        style={{ transform: `translateX(-${idx * 100}%)` }}
      >
        {images.map((img, i) => (
          <img key={i} src={img.src} alt={img.alt} className="w-full h-full object-cover shrink-0" />
        ))}
      </div>

      {images.length > 1 && (
        <>
          <button
            type="button"
            onClick={() => go(idx - 1)}
            aria-label="Previous photo"
            className="absolute left-2.5 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-[rgba(201,168,76,0.9)] flex items-center justify-center active:scale-90 hover:bg-[#C9A84C] transition-[transform,background-color] duration-150"
          >
            <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path d="M9 2L4 7L9 12" stroke="#0A1628" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => go(idx + 1)}
            aria-label="Next photo"
            className="absolute right-2.5 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-[rgba(201,168,76,0.9)] flex items-center justify-center active:scale-90 hover:bg-[#C9A84C] transition-[transform,background-color] duration-150"
          >
            <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path d="M5 2L10 7L5 12" stroke="#0A1628" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          <div className="absolute bottom-2.5 left-0 right-0 flex items-center justify-center gap-1.5">
            {images.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => go(i)}
                aria-label={`Go to photo ${i + 1}`}
                className={`h-1.5 rounded-full transition-all duration-300 ${i === idx ? 'w-5 bg-[#C9A84C]' : 'w-1.5 bg-white/40 hover:bg-white/60'}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Booking-hold countdown banner. Same markup as the shared HoldTimer, but sourced
// from the PARTNER booking context (the shared HoldTimer reads the customer
// context, so it can't be reused here). Kept local to stay partner-isolated.
function PartnerHoldTimer({ holdSeconds }) {
  const mm = String(Math.floor(holdSeconds / 60)).padStart(2, '0');
  const ss = String(holdSeconds % 60).padStart(2, '0');
  const urgent = holdSeconds > 0 && holdSeconds < 300;

  return (
    <div className="flex items-center justify-between gap-4 rounded-lg border border-[#D4A843] bg-[rgba(201,168,76,0.1)] px-5 py-4 mb-4 shrink-0">
      <div className="flex flex-col">
        <span className="font-dm-sans text-[13px] text-white/60">Booking held for</span>
        {holdSeconds > 0 ? (
          <span className={`font-cormorant text-3xl leading-none mt-1 tabular-nums ${urgent ? 'text-[#E5484D]' : 'text-[#C9A84C]'}`}>{mm}:{ss}</span>
        ) : (
          <span className="font-dm-sans text-[13px] font-medium text-[#C9A84C] mt-1.5">Your booking hold has expired</span>
        )}
      </div>
      <span className="font-dm-sans text-sm text-white font-medium text-right max-w-[160px] leading-relaxed">
        Complete the booking before time runs out
      </span>
    </div>
  );
}

export default function PartnerCruiseSummary() {
  const { partnerSession, partnerProfile, loadingAuth } = usePartnerAuth();
  const {
    selectedDate, passengers, selectedCruise, holdId, holdSeconds,
    tacRate, fading, go, exitFlow, exitToDashboard,
  } = usePartnerBooking();


  // Warning dialog shown ONLY when leaving toward an exit (this screen's back
  // control AND browser back). The FORWARD (Continue) button skips it.
  const [showLeaveWarning, setShowLeaveWarning] = useState(false);
  // Separate confirm for "Exit to Dashboard" (leaves to /partner and releases
  // the hold) — kept distinct from the back-exit warning above, which leaves to
  // availability via exitFlow.
  const [showExitWarning, setShowExitWarning] = useState(false);

  // Browser-back guard. Push a sentinel history entry so the first browser Back
  // pops to this same screen (staying put) and fires popstate, which we catch to
  // show the warning instead of silently leaving a dangling hold.
  useEffect(() => {
    if (!holdId) return;
    window.history.pushState(null, '', window.location.href);
    const onPop = () => {
      window.history.pushState(null, '', window.location.href); // re-arm — stay here
      setShowLeaveWarning(true);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [holdId]);

  // Partner discount rate (TAC %). Display only — the real charge is computed
  // server-side at payment (A3). The breakdown below uses the DISCOUNTED prices.
  const rate = Number(tacRate) > 0 ? Number(tacRate) : 0;
  const publicAdult = selectedCruise?.priceAdult ?? 0;
  const publicChild = selectedCruise?.priceChild ?? 0;
  const priceAdult = rate > 0 ? Math.round(publicAdult * (1 - rate / 100)) : publicAdult;
  const priceChild = rate > 0 ? Math.round(publicChild * (1 - rate / 100)) : publicChild;
  const adultsSubtotal = passengers.adults * priceAdult;
  const childrenSubtotal = passengers.children * priceChild;
  const grandTotal = adultsSubtotal + childrenSubtotal;
  const publicGrandTotal = passengers.adults * publicAdult + passengers.children * publicChild;
  const savings = publicGrandTotal - grandTotal;

  const cruiseCode = selectedCruise?.cruiseCode;
  const deckLabel = selectedCruise?.deckLabel;
  const cruiseName = selectedCruise?.cruiseName;
  // Guest-facing long name (display only); cruiseName above stays the key.
  const cruiseTitle = cruiseLabel(selectedCruise);

  // Internally-managed content, loaded from Supabase with the anon client:
  // cruise_settings (reporting time, public description, public terms),
  // ticket_settings (boarding point + map link), and media_gallery photos.
  // (Loader replicated verbatim from the customer CruiseSummary.js.)
  const [content, setContent] = useState(null);
  const [galleryImages, setGalleryImages] = useState(null);

  useEffect(() => {
    if (!cruiseCode) return;
    let cancelled = false;

    (async () => {
      // Times and boarding point come from the shared cruiseDetails lookup,
      // keyed on the cruise DATE the partner is booking for.
      const [csRes, times, boarding, deckRes] = await Promise.all([
        supabase
          .from('cruise_settings')
          .select('public_description, public_terms')
          .eq('cruise_code', cruiseCode)
          .maybeSingle(),
        fetchCruiseTimes({ cruiseCode, date: selectedDate }),
        fetchBoardingPoint({ date: selectedDate }),
        supabase
          .from('media_gallery')
          .select('storage_path, sort_order')
          .eq('owner_type', 'cruise_deck')
          .eq('owner_key', `${cruiseCode}|${deckLabel}`)
          .order('sort_order'),
      ]);

      // Gallery fallback chain: per-deck → per-cruise → local placeholders.
      let rows = deckRes.data || [];
      if (rows.length === 0) {
        const cruiseRes = await supabase
          .from('media_gallery')
          .select('storage_path, sort_order')
          .eq('owner_type', 'cruise')
          .eq('owner_key', cruiseCode)
          .order('sort_order');
        rows = cruiseRes.data || [];
      }
      const images = rows.length
        ? rows.map((r, i) => ({
            src: supabase.storage.from('site-media').getPublicUrl(r.storage_path).data.publicUrl,
            alt: `${cruiseTitle} ${displayDeck(cruiseName, deckLabel)} — onboard photo ${i + 1}`,
          }))
        : galleryFor(cruiseName, deckLabel);

      if (cancelled) return;
      const cs = csRes.data || {};
      const ts = boarding || {};
      setContent({
        reportingTime: to12h(times?.reporting_time),
        boardingPoint: ts.boarding_point || '',
        boardingMapsUrl: ts.boarding_maps_url || '',
        description: (cs.public_description || '').trim(),
        terms: (cs.public_terms || '').trim(),
      });
      setGalleryImages(images);
    })();

    return () => { cancelled = true; };
  }, [cruiseCode, deckLabel, cruiseName, cruiseTitle, selectedDate]);

  // Guards — wait for the auth check, then require an approved partner session
  // and an active hold (with its cruise + date). A refresh clears in-memory state
  // by design, so fall back to the partner availability screen.
  if (loadingAuth) {
    return (
      <div className="w-full max-w-[480px] p-px rounded-2xl bg-gradient-to-b from-[rgba(212,168,67,0.32)] via-[rgba(255,255,255,0.06)] to-[rgba(255,255,255,0.02)] card-enter">
        <div className="bg-[#0D1E35] rounded-[15px] shadow-[0_40px_80px_rgba(0,0,0,0.55)] px-7 py-8">
          <p className="font-dm-sans text-sm text-white/55 font-light leading-relaxed text-center">
            Loading...
          </p>
        </div>
      </div>
    );
  }
  if (!partnerSession) return <Navigate to="/partner/login" replace />;
  if (partnerProfile?.approval_status !== 'approved') return <Navigate to="/partner" replace />;
  if (!selectedCruise || selectedDate === null || !holdId) return <Navigate to="/partner/book/availability" replace />;

  return (
    <div className={`w-full max-w-[800px] md:max-w-[1100px] mx-auto px-0 md:px-2 card-enter transition-opacity duration-[250ms] md:flex-1 md:min-h-0 md:flex md:flex-col ${fading ? 'opacity-0' : 'opacity-100'}`}>

        {/* Header row: back arrow (warns before leaving) · Exit to Dashboard */}
        <div className="flex items-center justify-between mb-4 shrink-0">
          {/* Back arrow — exits the flow, so it warns first (does NOT silently leave) */}
          <button
            type="button"
            onClick={() => setShowLeaveWarning(true)}
            className="flex items-center justify-center w-8 h-8 rounded-full border border-[rgba(212,168,67,0.3)] hover:border-[rgba(212,168,67,0.65)] hover:bg-[rgba(212,168,67,0.06)] transition-[border-color,background-color] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)]"
            aria-label="Go back"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path d="M9 2L4 7L9 12" stroke="#D4A843" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          {/* Active hold on this step → confirm before releasing + exiting */}
          <button
            type="button"
            onClick={() => setShowExitWarning(true)}
            className="font-dm-sans text-[13px] font-medium text-[rgba(212,168,67,0.6)] hover:text-[#C9A84C] hover:underline underline-offset-2 transition-colors duration-200"
          >
            Exit to Dashboard
          </button>
        </div>

        {/* Booking-hold countdown — active from the moment the table was reserved */}
        <PartnerHoldTimer holdSeconds={holdSeconds} />

        {/* Content area: stacked on mobile, two equal columns filling height on desktop */}
        <div className="md:flex-1 md:min-h-0 md:grid md:grid-cols-2 md:gap-8 md:overflow-hidden">

          {/* LEFT COLUMN */}
          <div className="md:h-full md:flex md:flex-col md:min-h-0">

            {/* Cruise header */}
            <div className="md:shrink-0">
              <p className="font-dm-sans text-[12px] uppercase tracking-[0.18em] text-[rgba(201,168,76,0.85)] mb-1.5">
                Partner Booking
              </p>
              <h1 className="font-cormorant font-normal text-[2.2rem] md:text-[2.6rem] leading-[1.05] tracking-[-0.01em] text-white mb-2">
                {cruiseTitle}
              </h1>
              <p className="font-dm-sans text-sm md:text-base font-medium text-[#C9A84C] mb-1.5">
                {displayDeck(selectedCruise.cruiseName, selectedCruise.deckLabel)} · {selectedCruise.time}
              </p>
              <p className="font-dm-sans text-sm text-white/55 font-light mb-6 md:mb-5">
                {formatCardDate(selectedDate)}
              </p>
            </div>

            {/* Image gallery — fixed height on mobile, fills remaining height on desktop */}
            <div className="mb-8 md:mb-5 md:flex-1 md:min-h-0">
              {galleryImages ? (
                <CruiseGallery images={galleryImages} heightClass="h-[220px] md:h-full" />
              ) : (
                <div className="w-full h-[220px] md:h-full rounded-xl bg-[#162840] animate-pulse" />
              )}
            </div>

            {/* Pricing summary box — DISCOUNTED partner pricing */}
            <div className="rounded-xl border border-[#C9A84C] bg-[#162840] p-5 md:p-4 mb-8 md:mb-0 md:shrink-0">
              {rate > 0 && (
                <div className="flex items-center justify-between mb-2.5">
                  <span className="font-dm-sans text-[12px] uppercase tracking-[0.1em] font-medium text-white/50">
                    Partner rate
                  </span>
                  <span className="font-dm-sans text-[10px] font-bold uppercase tracking-[0.06em] text-[#0A1628] bg-[#C9A84C] rounded-full px-1.5 py-[3px] leading-none">
                    &minus;{rate}%
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between py-1.5">
                <span className="font-dm-sans text-sm font-light text-white/70">
                  {passengers.adults} {passengers.adults === 1 ? 'Adult' : 'Adults'} &times; <span className="text-white">&#8377;{priceAdult.toLocaleString()}</span>
                  {rate > 0 && (
                    <span className="ml-1.5 font-light text-white/35 line-through text-xs">&#8377;{publicAdult.toLocaleString()}</span>
                  )}
                </span>
                <span className="font-dm-sans text-sm font-light text-white tabular-nums">&#8377;{adultsSubtotal.toLocaleString()}</span>
              </div>
              {passengers.children > 0 && (
                <div className="flex items-center justify-between py-1.5">
                  <span className="font-dm-sans text-sm font-light text-white/70">
                    {passengers.children} {passengers.children === 1 ? 'Child' : 'Children'} &times; <span className="text-white">&#8377;{priceChild.toLocaleString()}</span>
                    {rate > 0 && (
                      <span className="ml-1.5 font-light text-white/35 line-through text-xs">&#8377;{publicChild.toLocaleString()}</span>
                    )}
                  </span>
                  <span className="font-dm-sans text-sm font-light text-white tabular-nums">&#8377;{childrenSubtotal.toLocaleString()}</span>
                </div>
              )}
              {passengers.infants > 0 && (
                <div className="flex items-center justify-between py-1.5">
                  <span className="font-dm-sans text-sm font-light text-white/70">
                    {passengers.infants} {passengers.infants === 1 ? 'Infant' : 'Infants'}
                  </span>
                  <span className="font-dm-sans text-sm font-light text-white/55">Complimentary</span>
                </div>
              )}
              <div className="h-px bg-[rgba(212,168,67,0.25)] my-3 md:my-2.5" />
              <div className="flex items-center justify-between">
                <span className="font-dm-sans text-sm font-medium text-white">Total <span className="text-white/55 font-light">(GST inclusive)</span></span>
                <span className="font-dm-sans text-xl font-bold text-[#C9A84C] tabular-nums">&#8377;{grandTotal.toLocaleString()}</span>
              </div>
              {rate > 0 && savings > 0 && (
                <p className="font-dm-sans text-[12px] font-light text-white/50 text-right mt-1.5">
                  You save &#8377;{savings.toLocaleString()} vs the public rate
                </p>
              )}
            </div>

          </div>

          {/* RIGHT COLUMN — internally scrollable, sticky Continue at the bottom */}
          <div className="md:h-full md:flex md:flex-col md:min-h-0">

            <div className="md:flex-1 md:min-h-0 md:overflow-y-auto md:pr-1">

              {/* About this cruise */}
              <h2 className="font-cormorant font-normal text-[1.7rem] md:text-[2rem] leading-tight text-white mb-4">
                About This Cruise
              </h2>
              {!content ? (
                <div className="rounded-xl border border-white/[0.09] bg-[#162840] p-4 mb-8 flex flex-col gap-3 animate-pulse">
                  <div className="h-4 w-2/3 rounded bg-white/[0.06]" />
                  <div className="h-4 w-1/2 rounded bg-white/[0.06]" />
                  <div className="h-4 w-3/4 rounded bg-white/[0.06]" />
                </div>
              ) : (
                <div className="rounded-xl border border-white/[0.09] bg-[#162840] p-4 mb-8 flex flex-col">

                  {/* Reporting Time — from cruise_settings.reporting_time */}
                  <div className="flex flex-col sm:flex-row sm:items-start sm:gap-4 py-3">
                    <span className="font-dm-sans text-[13px] uppercase tracking-[0.1em] font-medium text-[rgba(212,168,67,0.85)] sm:w-[140px] sm:shrink-0 mb-1 sm:mb-0">
                      Reporting Time
                    </span>
                    <span className="font-dm-sans text-sm font-light text-white leading-relaxed flex-1">
                      {content.reportingTime}
                    </span>
                  </div>

                  {/* Boarding — from ticket_settings.boarding_point, with an
                      optional Google Maps link from ticket_settings.boarding_maps_url */}
                  <div className="border-t border-white/[0.06]">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:gap-4 py-3">
                      <span className="font-dm-sans text-[13px] uppercase tracking-[0.1em] font-medium text-[rgba(212,168,67,0.85)] sm:w-[140px] sm:shrink-0 mb-1 sm:mb-0">
                        Boarding
                      </span>
                      <span className="font-dm-sans text-sm font-light text-white leading-relaxed flex-1 min-w-0 break-words">
                        {content.boardingPoint}
                      </span>
                    </div>
                    {content.boardingMapsUrl && (
                      <a
                        href={content.boardingMapsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block sm:ml-[156px] font-dm-sans text-[13px] font-medium text-[#C9A84C] hover:underline underline-offset-2 -mt-1 mb-1"
                      >
                        View on Google Maps &rarr;
                      </a>
                    )}
                  </div>

                  {/* Cruise description — cruise_settings.public_description as
                      free-text prose. Hidden entirely when empty. */}
                  {content.description && (
                    <div className="border-t border-white/[0.06] pt-3">
                      <p className="font-dm-sans text-sm font-light text-white/80 leading-relaxed whitespace-pre-line">
                        {content.description}
                      </p>
                    </div>
                  )}

                </div>
              )}

              {/* Cancellation policy */}
              <h2 className="font-cormorant font-normal text-[1.7rem] md:text-[2rem] leading-tight text-white mb-4">
                Cancellation Policy
              </h2>
              {!content ? (
                <div className="flex flex-col gap-2.5 mb-8 md:mb-4 animate-pulse">
                  <div className="h-3.5 w-full rounded bg-white/[0.06]" />
                  <div className="h-3.5 w-5/6 rounded bg-white/[0.06]" />
                  <div className="h-3.5 w-2/3 rounded bg-white/[0.06]" />
                </div>
              ) : content.terms ? (
                /* cruise_settings.public_terms as readable text (line breaks preserved) */
                <p className="font-dm-sans text-[13px] font-light text-white/60 leading-relaxed whitespace-pre-line mb-8 md:mb-4">
                  {content.terms}
                </p>
              ) : (
                /* Common default when this cruise has no public_terms set */
                <ul className="flex flex-col gap-2.5 mb-8 md:mb-4">
                  {[
                    'Cancellations made 24+ hours before cruise: 90% refund (10% cancellation fee applies)',
                    'Cancellations within 24 hours: No refund',
                    'Weather cancellations (Sunset / Sundown): Full refund of cruise charge',
                    'Weather cancellations (Dinner Cruise): No refund — activities proceed as scheduled',
                    'Tickets are non-transferable',
                  ].map((item, i) => (
                    <li key={i} className="flex gap-2.5">
                      <span className="mt-[7px] w-1 h-1 rounded-full bg-[rgba(212,168,67,0.7)] shrink-0" />
                      <span className="font-dm-sans text-[13px] font-light text-white/60 leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              )}

            </div>

            {/* Continue — sticky at the bottom of the right column on desktop */}
            <div className="md:shrink-0 md:sticky md:bottom-0 md:bg-[#0A1628] md:pt-3">
              <button
                type="button"
                onClick={() => go('/partner/book/passenger-details')}
                className="w-full py-[15px] bg-[#C9A84C] text-[#0A1628] font-dm-sans font-semibold text-[12.5px] tracking-[0.15em] uppercase rounded-full active:scale-[0.98] hover:bg-[#B8973E] transition-[transform,background-color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.5)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0A1628]"
              >
                Continue to Passenger Details
              </button>
              <p className="font-dm-sans text-center text-sm text-white/55 mt-3 mb-2 leading-relaxed">
                Complete the booking before time runs out
              </p>
            </div>

          </div>

        </div>

        {/* Leave-flow warning — only here, only toward an exit. Leave releases the hold. */}
        {showLeaveWarning && (
          <ModalShell
            ariaLabel="Leave booking — the seats are on hold"
            onClose={() => setShowLeaveWarning(false)}
          >
            {/* Ornamental diamond rule */}
            <div className="flex items-center gap-3 mb-4 w-full max-w-[180px]">
              <div className="flex-1 h-px bg-gradient-to-r from-transparent to-[rgba(212,168,67,0.4)]" />
              <div className="w-1.5 h-1.5 rotate-45 bg-[rgba(212,168,67,0.55)] shrink-0" />
              <div className="flex-1 h-px bg-gradient-to-l from-transparent to-[rgba(212,168,67,0.4)]" />
            </div>

            <h2 className="font-cormorant font-normal text-[1.8rem] leading-[1.1] tracking-[-0.01em] text-white mb-3">
              The seats are on hold
            </h2>
            <p className="font-dm-sans text-sm font-light text-white/60 leading-relaxed mb-6">
              If you leave now, they may not be available later. Leave anyway?
            </p>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={exitFlow}
                className="flex-1 py-[13px] bg-transparent border border-white/20 text-white/70 font-dm-sans font-semibold text-[12.5px] tracking-[0.15em] uppercase rounded-full transition-[transform,border-color,color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97] hover:border-white/40 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/25 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D1E35]"
              >
                Leave
              </button>
              <button
                type="button"
                onClick={() => setShowLeaveWarning(false)}
                className="flex-1 py-[13px] bg-[#C9A84C] text-[#0A1628] font-dm-sans font-semibold text-[12.5px] tracking-[0.15em] uppercase rounded-full transition-[transform,background-color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97] hover:bg-[#B8973E] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.5)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D1E35]"
              >
                Stay
              </button>
            </div>
          </ModalShell>
        )}

        {/* Exit-to-Dashboard confirm — releases the hold and leaves to /partner */}
        {showExitWarning && (
          <ModalShell
            ariaLabel="Leave booking — the seats are on hold"
            onClose={() => setShowExitWarning(false)}
          >
            {/* Ornamental diamond rule */}
            <div className="flex items-center gap-3 mb-4 w-full max-w-[180px]">
              <div className="flex-1 h-px bg-gradient-to-r from-transparent to-[rgba(212,168,67,0.4)]" />
              <div className="w-1.5 h-1.5 rotate-45 bg-[rgba(212,168,67,0.55)] shrink-0" />
              <div className="flex-1 h-px bg-gradient-to-l from-transparent to-[rgba(212,168,67,0.4)]" />
            </div>

            <h2 className="font-cormorant font-normal text-[1.8rem] leading-[1.1] tracking-[-0.01em] text-white mb-3">
              Leave booking?
            </h2>
            <p className="font-dm-sans text-sm font-light text-white/60 leading-relaxed mb-6">
              Your held seats will be released and this booking will not be completed. Leave to the dashboard?
            </p>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={exitToDashboard}
                className="flex-1 py-[13px] bg-transparent border border-white/20 text-white/70 font-dm-sans font-semibold text-[12.5px] tracking-[0.15em] uppercase rounded-full transition-[transform,border-color,color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97] hover:border-white/40 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/25 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D1E35]"
              >
                Leave
              </button>
              <button
                type="button"
                onClick={() => setShowExitWarning(false)}
                className="flex-1 py-[13px] bg-[#C9A84C] text-[#0A1628] font-dm-sans font-semibold text-[12.5px] tracking-[0.15em] uppercase rounded-full transition-[transform,background-color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97] hover:bg-[#B8973E] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(201,168,76,0.5)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D1E35]"
              >
                Stay
              </button>
            </div>
          </ModalShell>
        )}

      </div>
  );
}
